<i18n>
{
  "de": {
    "close": "Schließen",
    "unlock": "Schalte diesen Beitrag frei",
    "or": "oder",
    "logIntoYourAccount": "melde Dich an",
    "backToSelection": "Zurück zur Auswahl",
    "offerAccessToFollowers": "Folge {nickname} und erhalte kostenlosen Zugriff auf diesen Post",
    "offerNoAccess": "bietet aktuell keinen Zugriff auf diesen Beitrag.",
    "notAvailable": "Sorry(404) - Dieser Beitrag ist momentan nicht verfügbar."
  },
  "en": {
    "close": "Close",
    "unlock": "Unlock this item",
    "or": "or",
    "logIntoYourAccount": "log into your account",
    "backToSelection": "Back to selection",
    "offerAccessToFollowers": "Follow {nickname} and gain free access to this post",
    "offerNoAccess": "currently does not offer access to this item.",
    "notAvailable": "Sorry(404) - This post is currently not available."
  }
}
</i18n>

<template>
  <section class="section">
    <section v-if="!$fetchState.pending && user.singlePost">
      <modal
        v-if="!user.singlePost.locked"
        defaultFooter="false"
        @close="closeModalPost"
      >
        <profile-post
          slot="body"
          :user="artist"
          :isModal="isModal"
          :post="user.singlePost"
          :sticky="!!user.singlePost.sticky"
          :commentData="user.commentData"
          :instantlyOpenComments="shouldInstantlyOpenComments"
        />

        <div
          slot="footer"
          class="post-actions"
        >
          <socialShare
            :artist="artist"
            :post="user.singlePost"
            :isModal="isModal"
            :fullPath="fullPath"
            class="social-share"
          />
          <button
            type="button"
            class="button is-primary is-outlined is-medium"
            @click="closeModalPost"
          >
            {{ $t('close')
            }}
          </button>
        </div>
      </modal>
      <modal
        v-else
        defaultHeader="true"
        defaultFooter="false"
        @close="closeModalPost"
      >
        <div
          v-if="!showLoginOnly"
          slot="header"
        >
          <span v-if="isAuthenticated">{{ $t('unlock') }}, {{ user.nickname }}!</span>
          <span v-else>{{ $t('unlock') }} {{ $t('or') }} <span
            class="button login-button is-small ml-5"
            @click="showLoginOnly = true"
          >{{ $t('logIntoYourAccount') }}</span>
          </span>
        </div>
        <div
          v-else
          slot="header"
        >
          <span
            class="button is-small"
            @click="showLoginOnly = false"
          >{{ $t('backToSelection') }}</span>
        </div>
        <div
          v-if="!showLoginOnly"
          slot="body"
          class="post-body"
        >
          <div>
            <section
              v-if="!hasOffers && user.singlePost.limitToFollowers"
              class="single-post-hero"
            >
              <p class="title">
                {{ $t('offerAccessToFollowers', { nickname: artist.nickname }) }}
              </p>
              <followButton
                :user="user"
                :fullWidth="false"
                :withIcon="false"
                :singlePost="true"
                @showGlobalFollowerModal="closeModalPost"
              />
            </section>
            <section
              v-else-if="!hasOffers"
              class="single-post-hero"
            >
              <p class="title">
                {{ artist.nickname }}<br>{{ $t('offerNoAccess') }}
              </p>
            </section>
            <section v-else>
              <post-wizard
                :post="user.singlePost"
                :item="item"
                @cancelled="closeModalPost"
              />
            </section>
          </div>
        </div>
        <div
          v-else
          slot="body"
          class="post-body"
        >
          <div>
            <post-wizard
              :post="user.singlePost"
              :showLoginOnly="true"
              @cancelled="closeModalPost"
            />
          </div>
        </div>
        <div
          slot="footer"
          class="post-actions pt-15"
        >
          <socialShare
            :artist="artist"
            :post="user.singlePost"
            :isModal="isModal"
            :fullPath="fullPath"
            class="social-share"
          />
          <button
            type="button"
            class="button is-primary is-outlined is-medium"
            @click="closeModalPost"
          >
            {{ $t('close')
            }}
          </button>
        </div>
      </modal>
    </section>
    <section v-else-if="invalidPostId">
      <modal
        defaultFooter="true"
        @close="closeModalPost"
      >
        <div slot="body">
          <h2>{{ $t('notAvailable') }}</h2>
        </div>
      </modal>
    </section>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import profilePost from '~/components/profile-post'
import modal from '~/components/modal'
import socialShare from '~/components/social-share'
import postWizard from '~/components/post-wizard'
import followButton from '~/components/follow-button'

export default {
  components: {
    profilePost, modal, socialShare, postWizard, followButton
  },

  data() {
    return {
      invalidPostId: false,
      isModal: true,
      item: null,
      showLoginOnly: false,
      instantlyOpenComments: false
    }
  },

  // nuxt otherwise keeps pending for at least 200ms on the client, which
  // would delay the modal on in-app navigation compared to the old created()
  fetchDelay: 0,

  // runs on the server so the post's og tags are in the initial html,
  // re-runnable on the client through this.$fetch()
  async fetch() {
    const cookie = this.$nuxt.context.req
      ? this.$nuxt.context.req.headers.cookie
      : null

    try {
      const response = await this.apiGet(this.postUri, null, cookie)

      if (!response || !response.success) {
        this.invalidPostId = true
        return
      }

      this.$store.dispatch('setSinglePost', {
        post: response.post,
        commentData: response.commentData
      })
      this.invalidPostId = false
      this.instantlyOpenComments = this.$route.query.comments === 'init'
    } catch (e) {
      this.invalidPostId = true
      console.log('There was an error while requesting data fron the API', e)
    }
  },

  head() {
    const head = {
      bodyAttrs: {
        class: 'unscrollable'
      }
    }

    // the title is public even when the post is locked; profile-post sets the
    // same one, but only renders in the unlocked branch
    if (this.user.singlePost && this.user.singlePost.title) {
      head.title = `${this.artist.nickname} - ${this.user.singlePost.title}`
    }

    return head
  },

  created() {
    this.item = {
      itemType: this.$route.params.itemType,
      itemId: this.$route.params.itemId
    }
  },
  mounted() {
    this.$root.$on('refetchSinglePost', this.$fetch)
    this.$root.$on('closeModalPost', this.closeModalPost)
  },

  beforeRouteUpdate(to, from, next) {
    next()
    if (to.query.comments !== 'init') {
      this.checkRefresh()
    }
  },

  computed: {
    ...mapState(['user', 'artist', 'isAuthenticated', 'host']),
    postUri() {
      const params = this.$route.params
      return params.itemType === 'comment'
        ? `/api/public/${params.id}/post/${params.slug}/${params.postId}/comment/${params.itemId}`
        : `/api/public/${params.id}/post/${params.slug}/${params.postId}`
    },
    fullPath() {
      return `https://${this.host}${this.$route.fullPath}`
    },
    hasOffers() {
      return (
        this.user.singlePost &&
        (this.user.singlePost.minRewardAmount > 0 ||
          this.user.singlePost.cheapestProduct)
      )
    },
    shouldInstantlyOpenComments() {
      return this.instantlyOpenComments
    }
  },

  methods: {
    async closeModalPost() {
      const locale = await this.$i18n.locale
      this.$store.dispatch('clearSinglePost')
      this.$router.push({
        name: `id-posts___${locale}`,
        params: {
          id: this.artist.urlAlias,
          scrollTo: {
            target: 'profile-navigation'
          }
        }
      })
    },

    checkRefresh() {
      if (this.$route.query.refresh !== undefined) {
        this.$router.replace(this.$route.path)
        return
      }
      this.$fetch()
    }
  },
  beforeDestroy() {
    this.$root.$off('refetchSinglePost', this.$fetch)
    this.$root.$off('closeModalPost', this.closeModalPost)
  }
}
</script>

<style lang="scss">
@import 'assets/sass/_variables';

.unscrollable {
  overflow-y: hidden;
  max-height: 100vh !important;
}

.post-body {
  flex-grow: 2;
  height: 100%;
}

.post-actions {
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-end;
}

.bg-image-covered {
  background-size: cover;
  background-position: center center;
  height: 100%;
  filter: blur(5px);
}

.single-post-hero {
  padding: 20px 60px;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
}

span.error {
  color: var(--primary-color);
  font-size: 20px;
  display: flex;
  justify-content: center;
}

.login-button {
  margin-top: -2px;
}
</style>
